import { Col, Row } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { DATE_FORMAT, PROFESSIONAL_SITUATIONS } from "utils/constants";
import { hundredYearsAgoStart } from "utils/helpers/date";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import ActionButtons from "../Components/ActionButtons";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

const MUTUAL_MOBILITY_SITUATION_PROFESSIONAL = {
  [PROFESSIONAL_SITUATIONS.PRE_RETIRED]: PROFESSIONAL_SITUATIONS.PRE_RETIRED,
  [PROFESSIONAL_SITUATIONS.RETIRED]: PROFESSIONAL_SITUATIONS.RETIRED,
};

const getEligibleMembershipFromSituation = (situation) => {
  if (situation === PROFESSIONAL_SITUATIONS.RETIRED) return true;
  if (situation === PROFESSIONAL_SITUATIONS.PRE_RETIRED) return false;
  return undefined;
};

function View({ initialValues, locked, onNext, deltas, onBack }) {
  const { t } = useTranslation();
  const { t: f } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = f("B2C_MUTUAL_MOBILITY", {
    returnObjects: true,
  });

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues,
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        deltaRia: get(deltas, "start.ria"),
        deltaStart: get(deltas, "start.normal"),
        deltaEndRia: get(deltas, "end.ria"),
        deltaEnd: get(deltas, "end.normal"),
      },
    ),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  return (
    <Layout>
      <Header
        title="Réponse immédiate ! Obtenez votre offre en moins de <b>2 minutes</b>"
        highlight="Réponse immédiate !"
        subtitle="Informations de l’adhérent"
        onBack={onBack}
      />
      <Row justify="center">
        <Col xs={24} md={16} lg={16}>
          <div className="grid--1">
            <div className="grid--2">
              <Input
                type="date"
                format={DATE_FORMAT}
                placeholder={"JJ / MM / AAAA"}
                label={"Date de naissance"}
                disabledDate={(current) =>
                  current > dayjs().subtract(18, "years").endOf("year") ||
                  current < hundredYearsAgoStart
                }
                value={
                  get(values, "project.contact.user.birthDate")
                    ? dayjs(get(values, "project.contact.user.birthDate"))
                    : undefined
                }
                defaultPickerValue={
                  !get(values, "project.contact.user.birthDate") &&
                  dayjs().subtract(30, "years")
                }
                onChange={(date) => {
                  const major = dayjs().subtract(18, "years").endOf("year");
                  if (date?.isValid() && date.isBefore(major)) {
                    setFieldValue(
                      "project.contact.user.birthDate",
                      date
                        .set("hour", 2)
                        .set("minute", 0)
                        .set("second", 0)
                        .toISOString(),
                    );
                  }
                }}
                onBlur={({ target: { value } }) => {
                  const major = dayjs().subtract(18, "years").endOf("year");
                  const date = dayjs(value, DATE_FORMAT);
                  if (date?.isValid() && date.isBefore(major))
                    setFieldValue(
                      "birthDate",
                      date ? date.add(2, "h").toISOString() : null,
                    );
                }}
              />
              <Select
                options={Object.entries(
                  MUTUAL_MOBILITY_SITUATION_PROFESSIONAL,
                ).map(([key, value]) => ({
                  value,
                  label:
                    TRANSLATION.FORM.SITUATION_PROFESSIONAL.RENAULT_OPTIONS[
                      key
                    ],
                }))}
                label={TRANSLATION.FORM.SITUATION_PROFESSIONAL.LABEL}
                placeholder={
                  TRANSLATION.FORM.SITUATION_PROFESSIONAL.PLACEHOLDER
                }
                {...bindInputProps({
                  name: "project.contact.socioProfessionalCategory",
                  values,
                  ...formProps,
                })}
                onChange={(value) => {
                  setFieldValue(
                    "project.contact.socioProfessionalCategory",
                    value,
                  );
                  setFieldValue(
                    "project.contact.fields.eligibleMembership",
                    getEligibleMembershipFromSituation(value),
                  );
                }}
                disabled={locked}
              />
            </div>
            <div className="grid--2">
              <Input
                type="date"
                format={DATE_FORMAT}
                placeholder={"JJ / MM / AAAA"}
                label={"Date d’effet"}
                disabledDate={(current) => current < moment()}
                {...bindDateInputProps({
                  name: "project.contract.issuanceDate",
                  setFieldValue,
                  values,
                  ...formProps,
                })}
              />
            </div>
            <div className="form-field">
              <Input
                type="checkbox"
                label={
                  "J’autorise Mobilité <span>Mutuelle</span> à collecter et utiliser les données personnelles nécessaires à l’exécution de mon devis et une proposition adaptée à mes besoins."
                }
                checked={get(values, "project.contact.acceptContact") || false}
                {...bindInputProps({
                  name: "project.contact.acceptContact",
                  values,
                  ...formProps,
                })}
                touched
              />
            </div>
          </div>
        </Col>
      </Row>
      <ActionButtons
        disabled={!isEmpty(formProps?.errors)}
        onNext={handleSubmit}
      />
    </Layout>
  );
}

export default View;
